import React, { useState } from 'react';
import { ArrowUpDown, MapPin, Calendar, Heart, Type, Plus, X, Zap, Sparkles } from 'lucide-react';
import { Button } from './button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { getSortingPresets } from '../../lib/spotSorting';

export function SortSelector({ 
  sortMode, 
  sortOrder, 
  secondarySortMode,
  secondarySortOrder,
  enableSecondarySorting,
  onSortModeChange, 
  onSortOrderChange,
  onSecondarySortModeChange,
  onSecondarySortOrderChange,
  onEnableSecondarySortingChange,
  hasUserLocation = false,
  disabled = false 
}) {
  const [showPresets, setShowPresets] = useState(false);

  const sortOptions = [
    {
      value: 'proximity',
      label: 'Distance',
      icon: MapPin,
      description: hasUserLocation ? 'Du plus proche au plus loin' : 'Nécessite la géolocalisation',
      disabled: !hasUserLocation
    },
    {
      value: 'date',
      label: 'Date de création',
      icon: Calendar,
      description: 'Du plus récent au plus ancien'
    },
    {
      value: 'popularity',
      label: 'Popularité',
      icon: Heart,
      description: 'Du plus aimé au moins aimé'
    },
    {
      value: 'title',
      label: 'Nom alphabétique',
      icon: Type,
      description: 'Ordre alphabétique A-Z'
    }
  ];

  const presets = getSortingPresets().filter(preset => 
    !preset.requiresLocation || hasUserLocation
  );

  const getCurrentSortLabel = () => {
    const current = sortOptions.find(option => option.value === sortMode);
    return current ? current.label : 'Trier par';
  };

  const getSecondarySortLabel = () => {
    const current = sortOptions.find(option => option.value === secondarySortMode);
    return current ? current.label : 'Puis par';
  };

  const handleSortModeSelect = (newMode) => {
    onSortModeChange(newMode);
    
    // Set appropriate default sort order for each mode
    const defaultOrders = {
      'proximity': 'asc', // Closest first
      'date': 'desc', // Newest first
      'popularity': 'desc', // Most liked first
      'title': 'asc' // A-Z
    };
    
    onSortOrderChange(defaultOrders[newMode] || 'asc');
  };

  const handleSecondarySortModeSelect = (newMode) => {
    onSecondarySortModeChange(newMode);
    
    // Set appropriate default order
    const defaultOrders = {
      'proximity': 'asc',
      'date': 'desc',
      'popularity': 'desc',
      'title': 'asc'
    };
    
    onSecondarySortOrderChange(defaultOrders[newMode] || 'desc');
  };

  const toggleSortOrder = (isPrimary = true) => {
    if (isPrimary) {
      onSortOrderChange(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSecondarySortOrderChange(secondarySortOrder === 'asc' ? 'desc' : 'asc');
    }
  };

  const applyPreset = (preset) => {
    onSortModeChange(preset.primary.mode);
    onSortOrderChange(preset.primary.order);
    onSecondarySortModeChange(preset.secondary.mode);
    onSecondarySortOrderChange(preset.secondary.order);
    onEnableSecondarySortingChange(true);
    setShowPresets(false);
  };

  return (
    <div className="flex items-center space-x-2">
      {/* Presets Toggle */}
      <DropdownMenu open={showPresets} onOpenChange={setShowPresets}>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            disabled={disabled}
            className="flex items-center space-x-1 text-xs"
          >
            <Sparkles size={12} />
            <span>Presets</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuLabel className="flex items-center space-x-2">
            <Zap size={16} />
            <span>Tris intelligents</span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {presets.map((preset) => (
            <DropdownMenuItem
              key={preset.id}
              onClick={() => applyPreset(preset)}
              className="flex items-start space-x-3 p-3 cursor-pointer"
            >
              <span className="text-lg">{preset.icon}</span>
              <div className="flex-1">
                <div className="font-medium text-sm">
                  {preset.name}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {preset.description}
                </div>
              </div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Primary Sort */}
      <div className="flex items-center space-x-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              size="sm" 
              disabled={disabled}
              className="flex items-center space-x-2 text-xs"
            >
              <ArrowUpDown size={14} />
              <span>{getCurrentSortLabel()}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Trier les spots par</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {sortOptions.map((option) => {
              const Icon = option.icon;
              return (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => !option.disabled && handleSortModeSelect(option.value)}
                  className={`flex items-start space-x-3 p-3 cursor-pointer ${
                    option.disabled ? 'opacity-50 cursor-not-allowed' : ''
                  } ${sortMode === option.value ? 'bg-blue-50 border-l-2 border-blue-500' : ''}`}
                  disabled={option.disabled}
                >
                  <Icon size={16} className="mt-0.5 text-gray-600" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">
                      {option.label}
                      {sortMode === option.value && (
                        <span className="ml-2 text-blue-600">✓</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {option.description}
                    </div>
                  </div>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Primary Sort Order Toggle */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => toggleSortOrder(true)}
          disabled={disabled}
          className="flex items-center space-x-1 text-xs px-2"
          title={sortOrder === 'asc' ? 'Croissant' : 'Décroissant'}
        >
          <ArrowUpDown size={12} className={`transform transition-transform ${
            sortOrder === 'desc' ? 'rotate-180' : ''
          }`} />
        </Button>
      </div>

      {/* Secondary Sort Toggle */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onEnableSecondarySortingChange(!enableSecondarySorting)}
        disabled={disabled}
        className={`flex items-center space-x-1 text-xs px-2 ${
          enableSecondarySorting ? 'bg-blue-50 text-blue-700' : ''
        }`}
        title={enableSecondarySorting ? 'Désactiver tri secondaire' : 'Activer tri secondaire'}
      >
        {enableSecondarySorting ? <X size={12} /> : <Plus size={12} />}
        <span>puis</span>
      </Button>

      {/* Secondary Sort (when enabled) */}
      {enableSecondarySorting && (
        <div className="flex items-center space-x-1 pl-2 border-l border-gray-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                disabled={disabled}
                className="flex items-center space-x-2 text-xs bg-gray-50"
              >
                <span>{getSecondarySortLabel()}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Puis trier par</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {sortOptions.map((option) => {
                const Icon = option.icon;
                const isDisabled = option.disabled || option.value === sortMode;
                return (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => !isDisabled && handleSecondarySortModeSelect(option.value)}
                    className={`flex items-start space-x-3 p-3 cursor-pointer ${
                      isDisabled ? 'opacity-50 cursor-not-allowed' : ''
                    } ${secondarySortMode === option.value ? 'bg-green-50 border-l-2 border-green-500' : ''}`}
                    disabled={isDisabled}
                  >
                    <Icon size={16} className="mt-0.5 text-gray-600" />
                    <div className="flex-1">
                      <div className="font-medium text-sm">
                        {option.label}
                        {secondarySortMode === option.value && (
                          <span className="ml-2 text-green-600">✓</span>
                        )}
                        {option.value === sortMode && (
                          <span className="ml-2 text-gray-400">(déjà utilisé)</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {option.description}
                      </div>
                    </div>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Secondary Sort Order Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toggleSortOrder(false)}
            disabled={disabled}
            className="flex items-center space-x-1 text-xs px-2"
            title={secondarySortOrder === 'asc' ? 'Croissant' : 'Décroissant'}
          >
            <ArrowUpDown size={12} className={`transform transition-transform ${
              secondarySortOrder === 'desc' ? 'rotate-180' : ''
            }`} />
          </Button>
        </div>
      )}
    </div>
  );
}